import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { colors, gradients } from '../theme';
import GradientButton from '../components/GradientButton';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const checkRoleAndNavigate = async (uid: string) => {
    const snap = await getDoc(doc(db, 'users', uid));
    const role = snap.exists() ? snap.data()?.role : 'customer';
    if (role === 'technician') {
      navigate('/wrong-role', { replace: true });
    } else {
      navigate('/home', { replace: true });
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await checkRoleAndNavigate(result.user.uid);
    } catch (err: any) {
      setError(getFriendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const uid = result.user.uid;
      const snap = await getDoc(doc(db, 'users', uid));
      if (!snap.exists()) {
        await setDoc(doc(db, 'users', uid), {
          displayName: result.user.displayName || '',
          email: result.user.email || '',
          phone: result.user.phoneNumber || '',
          role: 'customer',
          plan: 'free',
          createdAt: serverTimestamp(),
        });
      }
      await checkRoleAndNavigate(uid);
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(getFriendlyError(err.code));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#120B1E',
        overflowY: 'auto',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Pink radial glow at top — matches iOS */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 380,
        background: 'radial-gradient(ellipse at 50% -20%, rgba(192,38,160,0.28) 0%, rgba(123,47,190,0.12) 50%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 20px 40px',
        position: 'relative',
      }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo + wordmark — matches iOS LoginView */}
        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          {/* Logo with glow */}
          <div style={{
            width: 90, height: 90,
            borderRadius: 20,
            margin: '0 auto 16px',
            boxShadow: '0 8px 32px rgba(192,38,160,0.5)',
            overflow: 'hidden',
          }}>
            <img src="/logo-dark.png" alt="SageTech" style={{ width: 90, height: 90, objectFit: 'cover', display: 'block' }} />
          </div>
          {/* Gradient wordmark */}
          <div style={{
            fontSize: 38, fontWeight: 800,
            background: 'linear-gradient(90deg, #E040A0, #9333EA)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.5px',
            marginBottom: 8,
          }}>SageTech</div>
          <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: 15 }}>
            Expert tech help, at your door.
          </p>
        </div>

        {/* Form Card */}
        <div
          style={{
            background: "rgba(255,255,255,0.07)",
            borderRadius: 20,
            padding: 28,
            boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
          }}
        >
          <h2
            style={{
              color: "#fff",
              fontSize: 22,
              fontWeight: 700,
              margin: '0 0 24px',
              textAlign: 'center',
            }}
          >
            Welcome back
          </h2>

          <form onSubmit={handleEmailSignIn} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, display: 'block', marginBottom: 6 }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, display: 'block', marginBottom: 6 }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={inputStyle}
              />
            </div>

            {error && (
              <p style={{ color: colors.error, fontSize: 14, margin: 0, textAlign: 'center' }}>{error}</p>
            )}

            <GradientButton type="submit" loading={loading} fullWidth style={{ marginTop: 6 }}>
              Sign In
            </GradientButton>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: colors.divider }} />
            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>or</span>
            <div style={{ flex: 1, height: 1, background: colors.divider }} />
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '13px 20px',
              background: 'rgba(255,255,255,0.06)',
              border: '1.5px solid rgba(255,255,255,0.12)',
              borderRadius: 14,
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: googleLoading ? 'not-allowed' : 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
              opacity: googleLoading ? 0.6 : 1,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
              <path d="M44 24.6c0-1.4-.1-2.8-.4-4.1H24v7.8h11.3c-.5 2.6-2 4.8-4.1 6.3v5.2h6.6C41.7 36 44 30.7 44 24.6z" fill="#4285F4"/>
              <path d="M24 45c5.6 0 10.3-1.8 13.7-4.9l-6.6-5.2c-1.9 1.3-4.2 2-7.1 2-5.5 0-10.1-3.7-11.8-8.7H5.4v5.3C8.8 40.8 16 45 24 45z" fill="#34A853"/>
              <path d="M12.2 28.2c-.4-1.3-.7-2.7-.7-4.2s.2-2.9.7-4.2v-5.3H5.4C3.9 17.5 3 20.6 3 24s.9 6.5 2.4 9.5l6.8-5.3z" fill="#FBBC05"/>
              <path d="M24 10.7c3.1 0 5.8 1.1 8 3.1l5.9-5.9C34.3 4.6 29.6 2.7 24 2.7 16 2.7 8.8 6.9 5.4 13.5l6.8 5.3c1.7-5 6.3-8.1 11.8-8.1z" fill="#EA4335"/>
            </svg>
            {googleLoading ? 'Signing in…' : 'Continue with Google'}
          </button>

          <p style={{ textAlign: 'center', margin: '20px 0 0', fontSize: 14, color: "rgba(255,255,255,0.6)" }}>
            New here?{' '}
            <Link
              to="/signup"
              style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none' }}
            >
              Create account
            </Link>
          </p>
        </div>

        {/* Tech Link */}
        <p style={{ textAlign: 'center', marginTop: 28, fontSize: 13, color: colors.textTertiary }}>
          Are you a tech?{' '}
          <a
            href="https://apps.apple.com/app/sagetechpro"
            target="_blank"
            rel="noreferrer"
            style={{ color: "rgba(255,255,255,0.6)", fontWeight: 600, textDecoration: 'none' }}
          >
            Download SageTech Pro →
          </a>
        </p>
      </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '13px 14px',
  background: 'rgba(255,255,255,0.06)',
  border: '1.5px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  color: "#fff",
  fontSize: 15,
  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
};

function getFriendlyError(code: string): string {
  switch (code) {
    case 'auth/user-not-found': return 'No account with that email.';
    case 'auth/wrong-password': return 'Incorrect password.';
    case 'auth/invalid-email': return 'Invalid email address.';
    case 'auth/too-many-requests': return 'Too many attempts. Try again later.';
    case 'auth/invalid-credential': return 'Invalid email or password.';
    default: return 'Sign in failed. Please try again.';
  }
}
